
import { useState } from 'react'
import { Badge, tierConfig } from '../../../mocks/badges'
import { ReputationLevel } from '../../../mocks/badges'
import UserBadge from '../../../components/base/UserBadge'

interface BadgeShowcaseProps {
  unlockedBadges: Badge[]
  lockedBadges: Badge[]
  level: ReputationLevel
  nextLevel: ReputationLevel | null
  progress: number
  reputation: number
  totalBadges: number
}

type FilterCategory = 'all' | 'posting' | 'engagement' | 'social' | 'reputation' | 'special'

const categoryLabels: Record<FilterCategory, { label: string; icon: string }> = {
  all: { label: 'All', icon: 'ri-apps-line' },
  posting: { label: 'Posting', icon: 'ri-edit-line' },
  engagement: { label: 'Engagement', icon: 'ri-chat-3-line' },
  social: { label: 'Social', icon: 'ri-group-line' },
  reputation: { label: 'Reputation', icon: 'ri-award-line' },
  special: { label: 'Special', icon: 'ri-sparkling-line' },
}

export const BadgeShowcase = ({
  unlockedBadges,
  lockedBadges,
  level,
  nextLevel,
  progress,
  reputation,
  totalBadges,
}: BadgeShowcaseProps) => {
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all')
  const [showLocked, setShowLocked] = useState(false)

  const filteredUnlocked = activeFilter === 'all'
    ? unlockedBadges
    : unlockedBadges.filter(b => b.category === activeFilter)

  const filteredLocked = activeFilter === 'all'
    ? lockedBadges
    : lockedBadges.filter(b => b.category === activeFilter)

  return (
    <div className="space-y-6">
      {/* Reputation Level Card */}
      <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 rounded-xl p-5 border border-amber-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-xl ${level.bgColor} flex items-center justify-center`}>
              <i className={`${level.icon} text-2xl ${level.color}`}></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Level {level.level}: {level.name}</h3>
              <p className="text-sm text-gray-500">{reputation} reputation points</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-600">{unlockedBadges.length}<span className="text-sm text-gray-400 font-normal">/{totalBadges}</span></p>
            <p className="text-xs text-gray-500">Badges earned</p>
          </div>
        </div>

        {nextLevel && (
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span>{level.name}</span>
              <span>{nextLevel.minPoints - reputation} pts to {nextLevel.name}</span>
            </div>
            <div className="w-full h-2.5 bg-white rounded-full overflow-hidden border border-amber-100">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
        {!nextLevel && (
          <p className="text-sm text-amber-600 font-medium mt-1">
            <i className="ri-vip-diamond-line mr-1"></i>Maximum level reached!
          </p>
        )}
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(categoryLabels) as FilterCategory[]).map((cat) => {
          const cfg = categoryLabels[cat]
          const count = cat === 'all'
            ? unlockedBadges.length
            : unlockedBadges.filter(b => b.category === cat).length
          return (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === cat
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${cfg.icon} mr-1.5`}></i>
              {cfg.label}
              {count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeFilter === cat ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Unlocked Badges */}
      {filteredUnlocked.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <i className="ri-trophy-line mr-2 text-amber-500"></i>
            Earned Badges ({filteredUnlocked.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredUnlocked.map((badge) => {
              const tier = tierConfig[badge.tier]
              return (
                <div
                  key={badge.id}
                  className={`flex items-center space-x-3 p-3 rounded-xl border ${badge.borderColor} ${badge.bgColor} hover:shadow-md transition-all cursor-pointer`}
                >
                  <UserBadge badge={badge} size="md" showTooltip={false} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 truncate">{badge.name}</p>
                    <span className={`inline-block text-[10px] font-medium ${tier.color}`}>{tier.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-trophy-line text-2xl text-gray-300"></i>
          </div>
          <p className="text-sm text-gray-500">No badges earned in this category yet</p>
        </div>
      )}

      {/* Locked Badges Toggle */}
      {filteredLocked.length > 0 && (
        <div>
          <button
            onClick={() => setShowLocked(!showLocked)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer mb-3"
          >
            <i className={`${showLocked ? 'ri-eye-off-line' : 'ri-eye-line'} mr-2`}></i>
            {showLocked ? 'Hide' : 'Show'} locked badges ({filteredLocked.length})
            <i className={`ri-arrow-${showLocked ? 'up' : 'down'}-s-line ml-1`}></i>
          </button>

          {showLocked && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filteredLocked.map((badge) => (
                <div
                  key={badge.id}
                  className="flex items-center space-x-3 p-3 rounded-xl border border-gray-200 bg-gray-50 opacity-60 cursor-default"
                >
                  <UserBadge badge={badge} size="md" locked showTooltip={false} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-500 truncate">{badge.name}</p>
                    <p className="text-[10px] text-gray-400 truncate">
                      <i className="ri-lock-line mr-0.5"></i>{badge.requirement}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BadgeShowcase
